export type UrlChangeType = 'pushState' | 'replaceState' | 'popstate' | 'hashchange';

export interface UrlChangeDetail {
  type: UrlChangeType;

  oldURL: string | null;

  newURL: string;

  state: unknown;
}

export type UrlChangeCallback = (detail: UrlChangeDetail) => void;

export type CleanupFunction = () => void;

export function observeUrlChanges(callback: UrlChangeCallback): CleanupFunction {
  const { history } = window;
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;
  let prevURL: string = window.location.href;

  const notify = (type: UrlChangeType, state: unknown): void => {
    const newURL: string = window.location.href;
    if (newURL === prevURL) return;

    const detail: UrlChangeDetail = {
      type,
      oldURL: prevURL,
      newURL,
      state,
    };

    try {
      callback(detail);
    } catch (error) {
      console.warn('URL change callback error:', error);
    }

    window.dispatchEvent(new CustomEvent('urlchange', { detail }));
    prevURL = newURL;
  };

  history.pushState = function patchedPushState(state: unknown, _title: string, _url?: string | URL | null): void {
    const result = originalPushState.call(this, state, _title, _url);
    notify('pushState', state);
    return result;
  };

  history.replaceState = function patchedReplaceState(
    state: unknown,
    _title: string,
    _url?: string | URL | null
  ): void {
    const result = originalReplaceState.call(this, state, _title, _url);
    notify('replaceState', state);
    return result;
  };

  const onPopState = (e: PopStateEvent): void => notify('popstate', e.state);
  const onHashChange = (): void => notify('hashchange', history.state);

  window.addEventListener('popstate', onPopState);
  window.addEventListener('hashchange', onHashChange);

  return function cleanup(): void {
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', onPopState);
    window.removeEventListener('hashchange', onHashChange);
  };
}
